import { renderIconButton } from 'BomdisoftComponents/buttons/render';
import { splitMentions } from 'BomdisoftComponents/comments/mentions';
import { attributes, type HtmlValue, html } from 'BomdisoftComponents/html/index';
import { ADD_ROUNDED_GLYPH, MORE_VERT_ROUNDED_GLYPH } from 'BomdisoftComponents/icons/glyphs';
import { renderIcon } from 'BomdisoftComponents/icons/render';
import { renderTextField } from 'BomdisoftComponents/inputs/field/render';
import { renderToggleButton, renderToggleGroup } from 'BomdisoftComponents/inputs/toggle-group/render';
import { renderMenu, renderMenuItem } from 'BomdisoftComponents/menu/render';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';

export const REACTION_EMOJI = ['👍', '❤️', '🎯', '🎉', '🤔', '👀'] as const;

export const VISIBLE_BEFORE_FOLD = 2;

export type CommentsFilter = 'open' | 'resolved' | 'unread';

export type CommentsComposer = 'foot' | 'single' | 'top';

export type CommentsFilterOption = { disabledReason?: string; value: CommentsFilter };

export type CommentsReaction = { count: number; emoji: string; isMine: boolean };

export type CommentsComment = {
  authorInitials: string;
  canChange?: boolean;
  authorLabel: string;
  body: string;
  id: string;
  isDeleted: boolean;
  mentions?: readonly CommentsPerson[];
  reactions: readonly CommentsReaction[];
  when: string;
};

export type CommentsAnchor = { isOff?: boolean; text: string };

export type CommentsThread = {
  anchor?: CommentsAnchor;
  comments: readonly CommentsComment[];
  hiddenCount?: number;
  id: string;
  isResolved: boolean;
};

export type CommentsPerson = { id: string; initials: string; name: string };

export type CommentsWords = {
  addReaction: string;
  deleteComment: string;
  deleted: string;
  editComment: string;
  filterLabel: string;
  filters: Readonly<Record<CommentsFilter, string>>;
  mention?: string;
  menu: string;
  newComment: string;
  reopen: string;
  reply: string;
  resolve: string;
  showEarlier?: (count: number) => string;
  showMore: (count: number) => string;
};

export type CommentsOptions = {
  canReact?: boolean;
  canResolve?: boolean;
  canWrite?: boolean;
  composer: CommentsComposer;
  editing?: string | null;
  empty?: HtmlValue;
  expanded?: readonly string[];
  filter: CommentsFilter;
  filters: readonly CommentsFilterOption[];
  hasPeopleSearch?: boolean;
  idOf: (part: string) => string;
  people?: readonly CommentsPerson[];
  testIdPrefix: string;
  threads: readonly CommentsThread[];
  tooltipIdOf: (part: string) => string;
  words: CommentsWords;
};

type Context = Required<Omit<CommentsOptions, 'composer' | 'empty' | 'filters' | 'threads'>>;

export const visibleThreads = (threads: readonly CommentsThread[], filter: CommentsFilter) => threads.filter(thread => (filter === 'resolved' ? thread.isResolved : !thread.isResolved));

export const renderComments = ({ canReact = true, canResolve = true, canWrite = true, composer, editing = null, empty, expanded = [], filter, filters, hasPeopleSearch = false, idOf, people = [], testIdPrefix, threads, tooltipIdOf, words }: CommentsOptions) => {
  const context: Context = { canReact: canWrite && canReact, canResolve: canWrite && canResolve, canWrite, editing: canWrite ? editing : null, expanded, filter, hasPeopleSearch: canWrite && hasPeopleSearch, idOf, people: canWrite ? people : [], testIdPrefix, tooltipIdOf, words };
  const composerField = canWrite ? renderField(context, { className: 'bomdisoft-comments-composer', part: 'composer', placeholder: words.newComment, testId: `${testIdPrefix}-comment-composer` }) : '';
  const listed = visibleThreads(threads, filter);
  return html`<div class="bomdisoft-comments">${composer === 'single' ? '' : renderFilter(context, filters)}${composer === 'top' ? composerField : ''}<div class="bomdisoft-comments-threads">${listed.length === 0 ? (empty ?? '') : listed.map(thread => renderThread(context, thread))}</div>${composer === 'foot' || (composer === 'single' && threads.length === 0) ? composerField : ''}${offersMentions(context) ? renderPeople(context) : ''}</div>`;
};

const offersMentions = ({ hasPeopleSearch, people }: Context) => hasPeopleSearch || people.length > 0;

const renderFilter = ({ filter, testIdPrefix, tooltipIdOf, words }: Context, filters: readonly CommentsFilterOption[]) =>
  renderToggleGroup({
    attributes: { 'aria-label': words.filterLabel },
    children: filters.map(({ disabledReason, value }) => {
      const label = words.filters[value];
      const button = renderToggleButton({
        attributes: { 'aria-label': disabledReason === undefined ? undefined : `${label}. ${disabledReason}`, 'data-testid': `${testIdPrefix}-seg-${value}`, disabled: disabledReason !== undefined },
        children: label,
        isPressed: filter === value,
        shape: 'pill',
        value,
      });
      return disabledReason === undefined ? button : renderTooltip({ children: button, id: tooltipIdOf(`filter-${value}`), title: disabledReason });
    }),
    className: 'bomdisoft-comments-filter',
    shape: 'pill',
  });

type FieldOptions = { autofocus?: boolean; className?: string; part: string; placeholder?: string; testId: string; value?: string };

const renderField = (context: Context, { autofocus, className, part, placeholder, testId, value }: FieldOptions) =>
  renderTextField({
    attributes: { autofocus, 'data-testid': testId, placeholder },
    className,
    endAdornment: offersMentions(context) ? renderMentionButton({ label: context.words.mention ?? '', testId: `${testId}-mention`, tooltipId: context.tooltipIdOf(`mention-${part}`) }) : undefined,
    id: context.idOf(part),
    value,
  });

const renderMentionButton = ({ label, testId, tooltipId }: { label: string; testId: string; tooltipId: string }) =>
  renderTooltip({ children: html`<button${attributes({ 'aria-expanded': 'false', 'aria-haspopup': 'listbox', 'aria-label': label, class: 'bomdisoft-comments-mention-button', 'data-testid': testId, type: 'button' })}>@</button>`, id: tooltipId, title: label });

export const earlierBreak = (thread: CommentsThread): number => (thread.hiddenCount !== undefined && thread.hiddenCount > 0 ? thread.hiddenCount : 0);

const renderThread = (context: Context, thread: CommentsThread) => {
  const { canResolve, canWrite, expanded, testIdPrefix, words } = context;
  if (thread.hiddenCount !== undefined) return renderPagedThread(context, thread);
  const shown = expanded.includes(thread.id) ? thread.comments : thread.comments.slice(0, VISIBLE_BEFORE_FOLD);
  const folded = thread.comments.length - shown.length;
  const anchor = thread.anchor === undefined ? '' : html`<span${attributes({ class: 'bomdisoft-comments-anchor', 'data-off': thread.anchor.isOff, 'data-testid': `${testIdPrefix}-thread-anchor-${thread.id}` })}>${thread.anchor.text}</span>`;
  const more = folded > 0 ? html`<button${attributes({ class: 'bomdisoft-comments-more', 'data-testid': `${testIdPrefix}-thread-more-${thread.id}`, type: 'button' })}>${words.showMore(folded)}</button>` : '';
  const foot = !canResolve ? '' : html`<div class="bomdisoft-comments-foot"><button${attributes({ class: 'bomdisoft-comments-action', 'data-testid': `${testIdPrefix}-thread-resolve-${thread.id}`, type: 'button' })}>${thread.isResolved ? words.reopen : words.resolve}</button></div>`;
  const reply = canWrite ? renderField(context, { part: `reply-${thread.id}`, placeholder: words.reply, testId: `${testIdPrefix}-thread-reply-${thread.id}` }) : '';
  return html`<div${attributes({ class: 'bomdisoft-comments-thread', 'data-resolved': thread.isResolved, 'data-testid': `${testIdPrefix}-thread-${thread.id}` })}>${anchor}${shown.map(comment => renderComment(context, comment))}${more}${foot}${reply}</div>`;
};

const renderPagedThread = (context: Context, thread: CommentsThread) => {
  const { canResolve, canWrite, testIdPrefix, words } = context;
  const hidden = earlierBreak(thread);
  const [first, ...rest] = thread.comments;
  const anchor = thread.anchor === undefined ? '' : html`<span${attributes({ class: 'bomdisoft-comments-anchor', 'data-off': thread.anchor.isOff, 'data-testid': `${testIdPrefix}-thread-anchor-${thread.id}` })}>${thread.anchor.text}</span>`;
  const label = words.showEarlier?.(hidden) ?? words.showMore(hidden);
  const gap = hidden > 0 ? html`<div class="bomdisoft-comments-break"><button${attributes({ class: 'bomdisoft-comments-break-chip', 'data-testid': `${testIdPrefix}-thread-earlier-${thread.id}`, type: 'button' })}>${label}</button></div>` : '';
  const foot = !canResolve ? '' : html`<div class="bomdisoft-comments-foot"><button${attributes({ class: 'bomdisoft-comments-action', 'data-testid': `${testIdPrefix}-thread-resolve-${thread.id}`, type: 'button' })}>${thread.isResolved ? words.reopen : words.resolve}</button></div>`;
  const reply = canWrite ? renderField(context, { part: `reply-${thread.id}`, placeholder: words.reply, testId: `${testIdPrefix}-thread-reply-${thread.id}` }) : '';
  return html`<div${attributes({ class: 'bomdisoft-comments-thread', 'data-resolved': thread.isResolved, 'data-testid': `${testIdPrefix}-thread-${thread.id}` })}>${anchor}${first === undefined ? '' : renderComment(context, first)}${gap}${rest.map(comment => renderComment(context, comment))}${foot}${reply}</div>`;
};

export const renderCommentBody = (body: string, mentions: readonly string[] = []): HtmlValue => splitMentions(body, mentions).map(part => (part.isMention ? html`<span class="bomdisoft-comments-mention">${part.text}</span>` : part.text));

const renderComment = (context: Context, comment: CommentsComment) => {
  const { canReact, canWrite, editing, idOf, testIdPrefix, tooltipIdOf, words } = context;
  const id = comment.id;
  const canChange = canWrite && comment.canChange !== false;
  const menuButton = renderIconButton({ attributes: { 'data-testid': `${testIdPrefix}-comment-menu-${id}` }, children: renderIcon(MORE_VERT_ROUNDED_GLYPH, { size: 12 }), className: 'bomdisoft-comments-menu-button', label: words.menu, size: 'small' });
  const who = html`<span class="bomdisoft-comments-who"><b>${comment.authorLabel}</b>${` ${comment.when}`}${canChange ? renderTooltip({ children: menuButton, id: tooltipIdOf(`menu-${id}`), title: words.menu }) : ''}</span>`;
  const content =
    editing === id
      ? renderField(context, { autofocus: true, part: `edit-${id}`, testId: `${testIdPrefix}-comment-edit-input-${id}`, value: comment.body })
      : comment.isDeleted
        ? html`<p class="bomdisoft-comments-deleted">${words.deleted}</p>`
        : html`<p class="bomdisoft-comments-text">${renderCommentBody(
            comment.body,
            comment.mentions?.map(person => person.name),
          )}</p>`;
  const menu = !canChange
    ? ''
    : renderMenu({
        children: [renderMenuItem({ children: words.editComment, testId: `${testIdPrefix}-comment-edit-${id}` }), renderMenuItem({ children: words.deleteComment, testId: `${testIdPrefix}-comment-delete-${id}`, variant: 'danger' })],
        id: idOf(`menu-${id}`),
      });
  const picker = !canReact
    ? ''
    : renderMenu({
        children: html`<span class="bomdisoft-comments-picker">${REACTION_EMOJI.map(emoji => html`<button${attributes({ 'aria-label': emoji, class: 'bomdisoft-comments-pick', 'data-testid': `${testIdPrefix}-pick-emoji-${emoji}`, type: 'button' })}>${emoji}</button>`)}</span>`,
        id: idOf(`picker-${id}`),
        label: words.addReaction,
        role: 'dialog',
      });
  return html`<div${attributes({ class: 'bomdisoft-comments-comment', 'data-testid': `${testIdPrefix}-comment-${id}` })}><span aria-hidden="true" class="bomdisoft-comments-avatar">${comment.authorInitials}</span><div class="bomdisoft-comments-body">${who}${content}${comment.isDeleted ? '' : renderReactions(context, comment)}</div>${menu}${picker}</div>`;
};

const renderReactions = ({ canReact, testIdPrefix, tooltipIdOf, words }: Context, comment: CommentsComment) => {
  if (!canReact) {
    if (comment.reactions.length === 0) return '';
    return html`<span class="bomdisoft-comments-reactions">${comment.reactions.map((reaction, index) => html`<span${attributes({ class: 'bomdisoft-comments-reaction', 'data-mine': reaction.isMine, 'data-testid': `${testIdPrefix}-reaction-${comment.id}-${index}` })}>${`${reaction.emoji} ${reaction.count}`}</span>`)}</span>`;
  }
  const chips = comment.reactions.map(
    (reaction, index) =>
      html`<button${attributes({ 'aria-pressed': reaction.isMine ? 'true' : 'false', class: 'bomdisoft-comments-reaction', 'data-mine': reaction.isMine, 'data-testid': `${testIdPrefix}-reaction-${comment.id}-${index}`, type: 'button' })}>${`${reaction.emoji} ${reaction.count}`}</button>`,
  );
  const add = html`<button${attributes({ 'aria-label': words.addReaction, class: 'bomdisoft-comments-reaction', 'data-testid': `${testIdPrefix}-add-reaction-${comment.id}`, type: 'button' })}>${renderIcon(ADD_ROUNDED_GLYPH, { size: 12 })}</button>`;
  return html`<span class="bomdisoft-comments-reactions">${chips}${renderTooltip({ children: add, id: tooltipIdOf(`react-${comment.id}`), title: words.addReaction })}</span>`;
};

export const renderPeopleRows = ({ idOf, testIdPrefix }: Pick<CommentsOptions, 'idOf' | 'testIdPrefix'>, people: readonly CommentsPerson[]) =>
  people.map(person =>
    renderMenuItem({
      children: html`<span aria-hidden="true" class="bomdisoft-comments-person-mark">${person.initials}</span><span class="bomdisoft-comments-person-name">${person.name}</span>`,
      containerRole: 'listbox',
      id: idOf(`person-${person.id}`),
      testId: `${testIdPrefix}-person-${person.id}`,
    }),
  );

const renderPeople = (context: Context) =>
  renderMenu({
    children: renderPeopleRows(context, context.people),
    className: 'bomdisoft-comments-people',
    id: context.idOf('people'),
    label: context.words.mention,
    matchAnchorWidth: true,
    role: 'listbox',
  });
